// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — KpiGrid
// Top-level KPI cards grid for the dashboard
// ══════════════════════════════════════════════════════════════════════════════

import { DollarSign, Wallet, AlertTriangle, Lightbulb, Shield } from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { Grid } from '@components/ui/Primitives';
import { StatCard } from '@components/ui/StatCard';

export function KpiGrid({ data, onDrillDown }) {
  const { theme } = useTheme();
  const { t } = useLang();

  // Calculate KPIs from live data
  const sales = data?.sales || [];
  const gaps = data?.gaps || [];
  const insights = data?.insights || [];
  const employees = data?.employees || [];

  const totalRevenue = sales.reduce((s, d) => s + parseFloat(d.total_revenue || 0), 0);
  const netProfit = totalRevenue * 0.382; // 38.2% estimated margin
  const wasteLeakage = gaps.reduce((s, g) => s + parseFloat(g.estimated_monetary_loss || 0), 0);
  const openInsightsCount = insights.length;
  const potentialSavings = insights.reduce((s, i) => s + parseFloat(i.expected_impact || 0), 0);

  // Process efficiency score
  const flaggedCount = employees.filter(e => e.is_flagged).length;
  const processEfficiency = employees.length > 0
    ? Math.round((1 - flaggedCount / employees.length) * 100)
    : 100;

  // Calculate trend (comparing first half vs second half of sales data)
  const half = Math.floor(sales.length / 2);
  const recentRev = sales.slice(0, half).reduce((s, d) => s + parseFloat(d.total_revenue || 0), 0);
  const oldRev = sales.slice(half).reduce((s, d) => s + parseFloat(d.total_revenue || 0), 0);
  const revTrend = oldRev > 0 ? (((recentRev - oldRev) / oldRev) * 100).toFixed(1) : 0;

  return (
    <Grid min={220}>
      <StatCard
        icon={<DollarSign size={20} color={theme.primary} />}
        label={t.totalRevenue}
        value={`${t.sar} ${(totalRevenue / 1000).toFixed(1)}K`}
        trend={revTrend > 0 ? `+${revTrend}%` : `${revTrend}%`}
        trendDirection={revTrend >= 0 ? 'up' : 'down'}
      />
      <StatCard
        icon={<Wallet size={20} color={theme.success} />}
        label={t.netProfit}
        value={`${t.sar} ${(netProfit / 1000).toFixed(1)}K`}
        trend="+15%"
        trendDirection="up"
        onClick={() => onDrillDown?.('profit')}
        accentColor={theme.success}
      />
      <StatCard
        icon={<AlertTriangle size={20} color={theme.danger} />}
        label={t.wasteLeakage}
        value={`${t.sar} ${(wasteLeakage / 1000).toFixed(1)}K`}
        subtext={`${gaps.length} ${t.activeGaps}`}
        onClick={() => onDrillDown?.('waste')}
        accentColor={theme.danger}
      />
      <StatCard
        icon={<Shield size={20} color={theme.info} />}
        label={t.processEfficiency}
        value={`${processEfficiency}%`}
        subtext={flaggedCount > 0 ? `${flaggedCount} ${t.flagged}` : t.clear}
        accentColor={theme.info}
      />
      <StatCard
        icon={<Lightbulb size={20} color={theme.warning} />}
        label={t.openInsights}
        value={openInsightsCount}
        subtext={`${t.sar} ${(potentialSavings / 1000).toFixed(1)}K ${t.potentialSavings}`}
        accentColor={theme.warning}
      />
    </Grid>
  );
}
